// Civilian life & living airbases (roadmap #42 + #43): client-only ambient
// scenery driven entirely by (world.seed, serverTime) so every client sees the
// same boats and whales at the same instant with ZERO wire traffic —
// the same determinism trick buildClouds uses. Nothing here shoots, scores, or
// collides, so a client with it hidden and one with it shown run identical
// simulations. The moment any of it becomes shootable (the Gold Train, Theme B
// #10) it moves server-side; this rendering is reused as-is.
//
// Reactive motion (gull scatter, ground-crew scatter) is deliberately NOT
// synced across clients — only the deterministic anchors are. Cosmetic
// divergence there is fine and safe.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { mergeBoxes, TEAM_INFO, CLASS_BUILDERS } from "./planes.js";
import { teamBasePos } from "./world.js";

// One shared unlit material for scene-lit voxel scenery (matches the terrain
// material's flat look without importing it).
const litMat = new THREE.MeshLambertMaterial({ vertexColors: true });

const TAU = Math.PI * 2;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class AmbientLife {
  constructor(scene, field, config, particles = null, audio = null) {
    this.scene = scene;
    this.field = field;
    this.particles = particles;
    this.audio = audio;
    this.cfg = config.ambient ?? {};
    this.baseCfg = config.ambientBase ?? {};
    this.t = 0;
    this.tmp = new THREE.Vector3();
    this.dummy = new THREE.Object3D();
    this.whaleSeenSlot = -1; // analytics dedupe: one "whale_seen" per surfacing
    this.onWhaleSeen = null; // main.js hook for the analytics event
    this.gullCryAt = 0;

    this._buildBoats();
    this._buildLighthouse();
    this._buildGulls();
    this._buildWhales();
    this._buildBases();
  }

  // --- determinism helpers --------------------------------------------------
  h(i, salt) {
    return hash2(i | 0, salt | 0, this.field.seed);
  }
  isWater(x, z) {
    return this.field.surfaceAt(x, z) <= this.field.waterLevel + 0.5;
  }

  // ==========================================================================
  // #42 Fishing boats — grey neutral hulls puttering radial water lanes.
  // ==========================================================================
  _buildBoats() {
    const n = this.cfg.fishingBoats ?? 0;
    this.boats = [];
    if (n <= 0) return;
    const geo = this._boatGeometry();
    for (let i = 0; i < n; i++) {
      const lane = this._findWaterLane(i);
      if (!lane) continue;
      const mesh = new THREE.Mesh(geo, litMat);
      this.scene.add(mesh);
      this.boats.push({
        mesh, ...lane,
        phase: this.h(i, 8101) * lane.len * 2, // stagger along the ping-pong
        bob: this.h(i, 8102) * TAU,
        smokeAt: this.h(i, 8103),
      });
    }
  }

  // A cut-down freighter: a small grey hull, wheelhouse, and stubby funnel.
  _boatGeometry() {
    const hull = 0x6b7079, hullDark = 0x4c525a, house = 0x9aa0a8;
    const glass = 0x8fb8cc, funnel = 0x3a3e45, rail = 0x2b2f36;
    return mergeBoxes([
      { x: 0, y: 0.7, z: 0, w: 13, h: 2.0, d: 3.4, color: hull },
      { x: 0, y: -0.6, z: 0, w: 13, h: 1.2, d: 3.7, color: hullDark },
      { x: 5.4, y: 1.3, z: 0, w: 2.6, h: 1.4, d: 2.6, color: hull }, // raised bow
      { x: -1.0, y: 2.1, z: 0, w: 4.0, h: 1.8, d: 3.0, color: house }, // wheelhouse
      { x: -0.4, y: 2.5, z: 0, w: 0.5, h: 0.9, d: 2.4, color: glass },
      { x: -3.4, y: 2.6, z: 0, w: 1.3, h: 1.8, d: 1.3, color: funnel },
      { x: -3.4, y: 3.6, z: 0, w: 1.5, h: 0.4, d: 1.5, color: 0x24272c },
      { x: 3.0, y: 1.5, z: 0, w: 0.2, h: 2.4, d: 0.2, color: rail }, // mast
    ]);
  }

  // Re-do of the server's findSupplyLane on the client: hash candidate radial
  // lanes offshore and keep the first that stays over open water end to end.
  _findWaterLane(i) {
    const R = this.field.radius;
    for (let a = 0; a < 8; a++) {
      const bearing = this.h(i * 8 + a, 8201) * TAU;
      const r0 = R * (0.55 + this.h(i * 8 + a, 8202) * 0.12);
      const r1 = R * (0.78 + this.h(i * 8 + a, 8203) * 0.12);
      const spread = (this.h(i * 8 + a, 8204) - 0.5) * 0.8;
      const p0 = { x: Math.cos(bearing) * r0, z: Math.sin(bearing) * r0 };
      const p1 = { x: Math.cos(bearing + spread) * r1, z: Math.sin(bearing + spread) * r1 };
      let ok = true;
      for (let s = 0; s <= 6; s++) {
        const f = s / 6;
        if (!this.isWater(p0.x + (p1.x - p0.x) * f, p0.z + (p1.z - p0.z) * f)) {
          ok = false;
          break;
        }
      }
      if (ok) {
        const len = Math.hypot(p1.x - p0.x, p1.z - p0.z);
        return { p0, p1, len, speed: this.cfg.boatSpeedMS ?? 4 };
      }
    }
    return null;
  }

  _updateBoats(dt) {
    const water = this.field.waterLevel;
    for (const b of this.boats) {
      // Ping-pong scalar distance along the lane (deterministic from svrT).
      const cycle = 2 * b.len;
      let s = ((this.t * b.speed + b.phase) % cycle + cycle) % cycle;
      const fwd = s <= b.len;
      const f = fwd ? s / b.len : 2 - s / b.len;
      const x = b.p0.x + (b.p1.x - b.p0.x) * f;
      const z = b.p0.z + (b.p1.z - b.p0.z) * f;
      const ph = this.t * 0.9 + b.bob;
      b.mesh.position.set(x, water + Math.sin(ph) * 0.25, z);
      const dir = fwd ? 1 : -1;
      b.mesh.rotation.y = Math.atan2(-(b.p1.z - b.p0.z) * dir, (b.p1.x - b.p0.x) * dir);
      b.mesh.rotation.z = Math.sin(ph * 0.7) * 0.03;
      // A thin diesel plume off the funnel now and then.
      if (this.particles) {
        b.smokeAt -= dt;
        if (b.smokeAt <= 0) {
          b.smokeAt = 0.5;
          const yaw = b.mesh.rotation.y;
          this.tmp.set(x - 3.4 * Math.cos(yaw), water + 4.2, z + 3.4 * Math.sin(yaw));
          this.particles.spawn(this.tmp, {
            count: 1, speed: 2.2, life: 2.6, size: 0.6,
            colors: [0x9a9a9a, 0xb4b4b4, 0x868686], gravity: -2.5, drag: 0.8, grow: true,
          });
        }
      }
    }
  }

  // ==========================================================================
  // #42 Lighthouse — a striped coastal tower whose beam sweeps at night.
  // ==========================================================================
  _buildLighthouse() {
    this.lighthouse = null;
    const R = this.field.radius;
    // Highest coastal column in the 0.85–0.95 R shore band (hashed search).
    let best = null, bestH = -1e9;
    for (let i = 0; i < 240; i++) {
      const ang = this.h(i, 8301) * TAU;
      const r = R * (0.85 + this.h(i, 8302) * 0.1);
      const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
      const h = this.field.surfaceAt(x, z);
      if (h > this.field.waterLevel + 3 && h > bestH) {
        bestH = h;
        best = { x, z, y: h };
      }
    }
    if (!best) return;
    const white = 0xe8e6df, red = 0xc23a34, glass = 0xfff3c4, cap = 0x2b2f36;
    const tower = new THREE.Mesh(
      mergeBoxes([
        { x: 0, y: 2, z: 0, w: 9, h: 4, d: 9, color: 0x74706a }, // rock base
        { x: 0, y: 9, z: 0, w: 5.4, h: 10, d: 5.4, color: white },
        { x: 0, y: 6, z: 0, w: 5.7, h: 3, d: 5.7, color: red }, // barber stripes
        { x: 0, y: 12, z: 0, w: 5.7, h: 3, d: 5.7, color: red },
        { x: 0, y: 15.4, z: 0, w: 6.2, h: 1.2, d: 6.2, color: cap }, // gallery
        { x: 0, y: 17.2, z: 0, w: 4.2, h: 2.6, d: 4.2, color: glass }, // lantern room
        { x: 0, y: 19.2, z: 0, w: 5.0, h: 1.4, d: 5.0, color: cap }, // roof
      ]),
      litMat,
    );
    tower.position.set(best.x, best.y, best.z);
    this.scene.add(tower);
    // The rotating beam reuses the searchlight beam recipe: an additive cone
    // pointing down local +X, spun by the lantern head.
    const head = new THREE.Group();
    head.position.set(best.x, best.y + 17.5, best.z);
    this.scene.add(head);
    const beamGeo = new THREE.CylinderGeometry(24, 0.8, 900, 10, 1, true);
    beamGeo.rotateZ(-Math.PI / 2);
    beamGeo.translate(450, 0, 0);
    const beam = new THREE.Mesh(
      beamGeo,
      new THREE.MeshBasicMaterial({
        color: 0xfff3c4, transparent: true, opacity: 0.12,
        blending: THREE.AdditiveBlending, depthWrite: false, fog: false, side: THREE.DoubleSide,
      }),
    );
    beam.rotation.z = 0.02; // a hair below horizontal
    beam.visible = false;
    head.add(beam);
    // A warm lamp glow at the lantern, lit at night.
    const lamp = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 2.2, 2.2),
      new THREE.MeshBasicMaterial({ color: 0xfff0b0, transparent: true, opacity: 0.9, fog: false }),
    );
    lamp.position.copy(head.position);
    lamp.visible = false;
    this.scene.add(lamp);
    this.lighthouse = { head, beam, lamp };
  }

  _updateLighthouse(sky) {
    const l = this.lighthouse;
    if (!l) return;
    const night = sky?.nightK ?? 0;
    const on = night > 0.35;
    l.beam.visible = on;
    l.lamp.visible = on;
    if (on) {
      l.head.rotation.y = this.t * 0.5;
      l.beam.material.opacity = 0.08 + 0.08 * night;
    }
  }

  // ==========================================================================
  // #42 Seagull flocks — boids around hashed coastal anchors, one InstancedMesh.
  // ==========================================================================
  _buildGulls() {
    const flocks = this.cfg.gullFlocks ?? 0;
    this.gulls = null;
    if (flocks <= 0) return;
    const perFlock = 12;
    const total = flocks * perFlock;
    const birdGeo = mergeBoxes([
      { x: 0, y: 0, z: 0.9, w: 0.5, h: 0.14, d: 1.6, color: 0xf2f2f2 },
      { x: 0, y: 0, z: -0.9, w: 0.5, h: 0.14, d: 1.6, color: 0xf2f2f2 },
      { x: 0.5, y: 0.05, z: 0, w: 0.8, h: 0.24, d: 0.4, color: 0xdedede },
    ]);
    const mesh = new THREE.InstancedMesh(
      birdGeo,
      new THREE.MeshLambertMaterial({ color: 0xffffff }),
      total,
    );
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    this.scene.add(mesh);
    const R = this.field.radius;
    const anchors = [];
    for (let fi = 0; fi < flocks; fi++) {
      // Coastal anchor: sample the shore band for a point just above the water.
      let a = null;
      for (let s = 0; s < 60 && !a; s++) {
        const ang = this.h(fi * 60 + s, 8401) * TAU;
        const r = R * (0.4 + this.h(fi * 60 + s, 8402) * 0.5);
        const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
        const h = this.field.surfaceAt(x, z);
        if (h <= this.field.waterLevel + 6) a = { x, z };
      }
      if (!a) a = { x: Math.cos(fi) * R * 0.6, z: Math.sin(fi) * R * 0.6 };
      anchors.push({ ...a, y: this.field.waterLevel + 40 + this.h(fi, 8403) * 30, flee: 0 });
    }
    const birds = [];
    for (let fi = 0; fi < flocks; fi++) {
      for (let bi = 0; bi < perFlock; bi++) {
        const g = this.h(fi * perFlock + bi, 8404);
        const g2 = this.h(fi * perFlock + bi, 8405);
        birds.push({
          flock: fi,
          pos: new THREE.Vector3(
            anchors[fi].x + (g - 0.5) * 60,
            anchors[fi].y + (g2 - 0.5) * 20,
            anchors[fi].z + (this.h(fi * perFlock + bi, 8406) - 0.5) * 60,
          ),
          vel: new THREE.Vector3((g - 0.5) * 8, 0, (g2 - 0.5) * 8),
          phase: g * TAU,
        });
      }
    }
    this.gulls = { mesh, anchors, birds, perFlock };
  }

  _updateGulls(dt, probes) {
    const G = this.gulls;
    if (!G) return;
    // Scatter: a fast low plane near a flock anchor sends the whole flock
    // fleeing for 6 s. Reactive only — never synced.
    for (const a of G.anchors) {
      if (a.flee > 0) a.flee -= dt;
      for (const p of probes) {
        const dx = p.x - a.x, dz = p.z - a.z;
        if (p.spd > 45 && dx * dx + dz * dz < 120 * 120 && Math.abs(p.y - a.y) < 90) {
          a.flee = 6;
          break;
        }
      }
    }
    const sep = new THREE.Vector3();
    for (const b of G.birds) {
      const a = G.anchors[b.flock];
      // Cohesion toward the anchor, gentle bob, light separation.
      b.vel.x += (a.x - b.pos.x) * 0.15 * dt;
      b.vel.z += (a.z - b.pos.z) * 0.15 * dt;
      b.vel.y += (a.y - b.pos.y) * 0.2 * dt;
      sep.set(0, 0, 0);
      for (const o of G.birds) {
        if (o === b || o.flock !== b.flock) continue;
        const dx = b.pos.x - o.pos.x, dy = b.pos.y - o.pos.y, dz = b.pos.z - o.pos.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 36 && d2 > 1e-3) {
          const inv = 1 / d2;
          sep.x += dx * inv; sep.y += dy * inv; sep.z += dz * inv;
        }
      }
      b.vel.addScaledVector(sep, 12 * dt);
      if (a.flee > 0) {
        // Radial flee impulse away from the anchor centre.
        b.vel.x += (b.pos.x - a.x) * 0.6 * dt;
        b.vel.z += (b.pos.z - a.z) * 0.6 * dt;
        b.vel.y += 6 * dt;
      }
      // Speed clamp + a little wander so the flock never freezes.
      b.phase += dt * 3;
      b.vel.x += Math.cos(b.phase) * 2 * dt;
      b.vel.z += Math.sin(b.phase * 1.3) * 2 * dt;
      const sp = b.vel.length();
      const max = a.flee > 0 ? 34 : 16;
      if (sp > max) b.vel.multiplyScalar(max / sp);
      b.pos.addScaledVector(b.vel, dt);
      if (b.pos.y < this.field.waterLevel + 8) b.pos.y = this.field.waterLevel + 8;
    }
    // Write instance matrices: banked toward the flight direction, wings flapping.
    let idx = 0;
    for (const b of G.birds) {
      this.dummy.position.copy(b.pos);
      this.dummy.rotation.set(Math.sin(b.phase) * 0.35, Math.atan2(-b.vel.z, b.vel.x), 0);
      this.dummy.updateMatrix();
      G.mesh.setMatrixAt(idx++, this.dummy.matrix);
    }
    G.mesh.instanceMatrix.needsUpdate = true;
  }

  // ==========================================================================
  // #42 Whales — a deterministic surfacing schedule, a dark back + a spout.
  // ==========================================================================
  _buildWhales() {
    const n = this.cfg.whales ?? 0;
    this.whales = [];
    if (n <= 0) return;
    const geo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 14, h: 2.6, d: 4.4, color: 0x2b3138 },
      { x: 5.5, y: -0.2, z: 0, w: 4, h: 1.8, d: 2.6, color: 0x2b3138 }, // head
      { x: -7.2, y: 0.6, z: 0, w: 2.2, h: 0.5, d: 5.2, color: 0x232a30 }, // flukes
      { x: 1, y: 1.2, z: 0, w: 2.2, h: 1.2, d: 0.4, color: 0x333b42 }, // dorsal hump
    ]);
    for (let i = 0; i < n; i++) {
      const mesh = new THREE.Mesh(geo, litMat);
      mesh.visible = false;
      this.scene.add(mesh);
      this.whales.push({ mesh, i, spoutAt: 0 });
    }
    this.whaleSlot = this.cfg.whaleSlotSec ?? 90;
    this.whaleChance = this.cfg.whaleChance ?? 0.15;
  }

  // A hashed open-water point at least 500 m offshore for whale i in slot n.
  _whaleSpot(i, n) {
    const R = this.field.radius;
    for (let s = 0; s < 40; s++) {
      const ang = hash2(i * 40 + s, n, (this.field.seed + 8501) >>> 0) * TAU;
      const r = R * (0.5 + hash2(i * 40 + s, n, (this.field.seed + 8502) >>> 0) * 0.4);
      const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
      if (
        this.isWater(x, z) &&
        this.isWater(x + 500, z) && this.isWater(x - 500, z) &&
        this.isWater(x, z + 500) && this.isWater(x, z - 500)
      ) {
        return { x, z, yaw: ang };
      }
    }
    return null;
  }

  _updateWhales(dt, localPos) {
    if (!this.whales.length) return;
    const slot = Math.floor(this.t / this.whaleSlot);
    const water = this.field.waterLevel;
    for (const wh of this.whales) {
      const roll = hash2(wh.i, slot, (this.field.seed + 8503) >>> 0);
      const active = roll < this.whaleChance;
      if (!active) {
        wh.mesh.visible = false;
        continue;
      }
      const spot = this._whaleSpot(wh.i, slot);
      if (!spot) {
        wh.mesh.visible = false;
        continue;
      }
      // Surface over ~8 s within the slot: rise, arc, and slip back under.
      const into = this.t - slot * this.whaleSlot;
      const dur = 8;
      if (into > dur) {
        wh.mesh.visible = false;
        continue;
      }
      const f = into / dur; // 0..1
      const arc = Math.sin(f * Math.PI); // up and back down
      wh.mesh.visible = true;
      wh.mesh.position.set(spot.x, water - 2.2 + arc * 3.2, spot.z);
      wh.mesh.rotation.set(-arc * 0.25, spot.yaw, 0);
      // A white spout at the top of the arc + an analytics ping if the local
      // plane is close enough to have seen it.
      if (this.particles && f > 0.4 && f < 0.62) {
        wh.spoutAt -= dt;
        if (wh.spoutAt <= 0) {
          wh.spoutAt = 0.12;
          this.tmp.set(spot.x + 5.5 * Math.cos(spot.yaw), water + 3, spot.z - 5.5 * Math.sin(spot.yaw));
          this.particles.spawn(this.tmp, {
            count: 2, speed: 9, life: 1.6, size: 0.9,
            colors: [0xffffff, 0xeef7fc, 0xd5ecfa], gravity: 10, drag: 0.5,
            dir: new THREE.Vector3(0, 1, 0), spread: 0.25,
          });
        }
      }
      if (this.whaleSeenSlot !== slot && localPos) {
        const dx = localPos.x - spot.x, dz = localPos.z - spot.z;
        if (dx * dx + dz * dz < 400 * 400) {
          this.whaleSeenSlot = slot;
          this.onWhaleSeen?.();
        }
      }
    }
  }

  // ==========================================================================
  // #43 Living airbases — control tower, windsocks, ground crew per base.
  // ==========================================================================
  // The graded airfield apron — MUST mirror World.airfield() in world.go so
  // the deck the client draws is the deck the server parks aircraft on.
  airfield(bx, bz) {
    const inward = Math.atan2(-bz, -bx);
    const ix = Math.cos(inward), iz = Math.sin(inward);
    const tx = -iz, tz = ix;
    const cx = bx + ix * 70, cz = bz + iz * 70;
    let y = this.field.waterLevel;
    for (let a = -90; a <= 90; a += 30) {
      for (let b = -50; b <= 50; b += 25) {
        const h = this.field.surfaceAt(cx + tx * a + ix * b, cz + tz * a + iz * b);
        if (h > y) y = h;
      }
    }
    return { cx, cz, y: y + 1, ix, iz, tx, tz };
  }

  _buildBases() {
    this.bases = [];
    for (let team = 0; team < 2; team++) {
      const { x: bx, z: bz } = teamBasePos(this.field, team);
      const af = this.airfield(bx, bz);
      const base = { team, x: bx, z: bz, ground: af.y, af };
      this._buildApron(base, af, TEAM_INFO[team]);
      this._buildTower(base, af, TEAM_INFO[team]);
      if (this.baseCfg.windsocks !== false) this._buildWindsock(base, af);
      this._buildCrew(base, af);
      this.bases.push(base);
    }
  }

  // Local apron frame → world XZ. lx runs down the runway (tangential), lz runs
  // from the apron centre toward the HQ (−inward).
  _lw(af, lx, lz) {
    return { x: af.cx + af.tx * lx - af.ix * lz, z: af.cz + af.tz * lx - af.iz * lz };
  }

  // The apron slab, its skirts, the runway, and two hangars — all built in the
  // local runway frame and rotated into place as one group.
  _buildApron(base, af, info) {
    const g = new THREE.Group();
    g.position.set(af.cx, af.y, af.cz);
    // Rotate local +X onto the runway (tangential) direction.
    g.rotation.y = Math.atan2(-af.tz, af.tx);
    const concrete = 0x9a9a94, apronEdge = 0x7f7f79, asphalt = 0x3b3d40;
    const skirt = 0x6b6b66;
    const B = [];
    // Deck slab (top at local y=0) plus a skirt dropping 60 m to hide the gap
    // down to the terrain (or the sea, for a coastal base).
    B.push({ x: 0, y: -1, z: 0, w: 200, h: 2, d: 118, color: concrete });
    B.push({ x: 0, y: -0.3, z: 0, w: 202, h: 0.8, d: 120, color: apronEdge });
    B.push({ x: 0, y: -31, z: 59, w: 202, h: 60, d: 3, color: skirt });
    B.push({ x: 0, y: -31, z: -59, w: 202, h: 60, d: 3, color: skirt });
    B.push({ x: 100, y: -31, z: 0, w: 3, h: 60, d: 120, color: skirt });
    B.push({ x: -100, y: -31, z: 0, w: 3, h: 60, d: 120, color: skirt });
    // Runway strip down the middle, threshold bars, dashed centreline.
    B.push({ x: 0, y: 0.15, z: 0, w: 168, h: 0.3, d: 26, color: asphalt });
    for (const end of [-80, 80]) {
      for (let k = -4; k <= 4; k++) {
        B.push({ x: end, y: 0.32, z: k * 2.4, w: 4, h: 0.12, d: 1.4, color: 0xe8e8e0 });
      }
    }
    for (let x = -70; x <= 70; x += 14) {
      B.push({ x, y: 0.32, z: 0, w: 6, h: 0.12, d: 1.0, color: 0xe8e8e0 });
    }
    // Two arched-roof hangars along the far (map-centre) edge, doors facing the
    // apron, wearing the team band over the door.
    for (const hx of [-30, 22]) {
      const hz = -40;
      B.push({ x: hx, y: 6, z: hz, w: 26, h: 12, d: 20, color: 0x8a8f8c });
      B.push({ x: hx, y: 12.6, z: hz, w: 27, h: 2.2, d: 21, color: 0x6f746f }); // arched roof cap
      B.push({ x: hx, y: 13.9, z: hz, w: 20, h: 1.2, d: 15, color: 0x656a65 });
      B.push({ x: hx, y: 5, z: hz + 10.2, w: 20, h: 10, d: 0.5, color: 0x2b2f33 }); // dark door opening
      B.push({ x: hx, y: 10.6, z: hz + 10.4, w: 21, h: 1.0, d: 0.4, color: info.color }); // team band
    }
    g.add(new THREE.Mesh(mergeBoxes(B), litMat));
    this.scene.add(g);
    base.apron = g;
  }

  _buildTower(base, af, info) {
    // Control tower at one corner of the apron: a stack with a glass cab, a lit
    // window band that comes up at dusk, and a blinking red beacon.
    const p = this._lw(af, -80, -34); // map-centre corner, off the runway
    const wall = 0x8b8f94, dark = 0x5a5e63, glass = 0x2a3238;
    const tower = new THREE.Mesh(
      mergeBoxes([
        { x: 0, y: 6, z: 0, w: 10, h: 12, d: 10, color: wall },
        { x: 0, y: 12.6, z: 0, w: 10.6, h: 1.4, d: 10.6, color: info.color },
        { x: 0, y: 16, z: 0, w: 8, h: 5, d: 8, color: dark },
        { x: 0, y: 18.4, z: 0, w: 8.6, h: 3.0, d: 8.6, color: glass },
        { x: 0, y: 20.4, z: 0, w: 9.0, h: 1.2, d: 9.0, color: dark },
        { x: 0, y: 22.2, z: 0, w: 0.6, h: 2.4, d: 0.6, color: 0x2b2f36 },
      ]),
      litMat,
    );
    tower.position.set(p.x, af.y, p.z);
    this.scene.add(tower);
    const lit = new THREE.Mesh(
      new THREE.BoxGeometry(8.8, 3.0, 8.8),
      new THREE.MeshBasicMaterial({ color: 0xffdf9a, transparent: true, opacity: 0.55, fog: false }),
    );
    lit.position.set(p.x, af.y + 18.4, p.z);
    lit.visible = false;
    this.scene.add(lit);
    const beacon = new THREE.Mesh(
      new THREE.BoxGeometry(1.0, 1.0, 1.0),
      new THREE.MeshBasicMaterial({ color: 0xff3b30, transparent: true, opacity: 0.95, fog: false }),
    );
    beacon.position.set(p.x, af.y + 23.6, p.z);
    this.scene.add(beacon);
    base.tower = { lit, beacon };
  }

  _buildWindsock(base, af) {
    // A pole + 3-segment sock by a runway threshold; points downwind, inflates.
    const p = this._lw(af, 80, 40);
    const pole = new THREE.Mesh(
      mergeBoxes([{ x: 0, y: 4, z: 0, w: 0.4, h: 8, d: 0.4, color: 0x8a8f94 }]),
      litMat,
    );
    pole.position.set(p.x, af.y, p.z);
    this.scene.add(pole);
    const sock = new THREE.Group();
    sock.position.set(p.x, af.y + 7.6, p.z);
    const seg = (x, r, color) =>
      new THREE.Mesh(mergeBoxes([{ x, y: 0, z: 0, w: 1.4, h: r, d: r, color }]), litMat);
    sock.add(seg(1.0, 1.6, 0xff7a2a));
    sock.add(seg(2.4, 1.2, 0xf2f2f2));
    sock.add(seg(3.8, 0.8, 0xff7a2a));
    this.scene.add(sock);
    base.windsock = { sock };
  }

  _buildCrew(base, af) {
    const n = this.baseCfg.groundCrew ?? 7;
    if (n <= 0) {
      base.crew = null;
      return;
    }
    const geo = mergeBoxes([
      { x: 0, y: 0.9, z: 0, w: 0.6, h: 1.8, d: 0.6, color: 0x3f5a3a }, // body
      { x: 0, y: 2.1, z: 0, w: 0.5, h: 0.5, d: 0.5, color: 0xcaa07a }, // head
    ]);
    // vertexColors so the uniform-green body / skin head show (gulls stay white).
    const mesh = new THREE.InstancedMesh(geo, litMat, n);
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    const figs = [];
    for (let i = 0; i < n; i++) {
      // Scatter across the apron deck, between the parked line and the hangars.
      const lx = (this.h(base.team * 100 + i, 8601) - 0.5) * 150;
      const lz = (this.h(base.team * 100 + i, 8602) - 0.5) * 70;
      const p = this._lw(af, lx, lz);
      figs.push({
        home: { x: p.x, z: p.z },
        pos: new THREE.Vector3(p.x, af.y, p.z),
        phase: this.h(base.team * 100 + i, 8603) * TAU,
        flee: 0, fleeDir: this.h(base.team * 100 + i, 8604) * TAU,
      });
    }
    base.crew = { mesh, figs, y: af.y };
  }

  _updateBase(dt, base, sky, probes, wind) {
    // Tower dusk lighting + beacon blink.
    if (base.tower) {
      const night = sky?.nightK ?? 0;
      base.tower.lit.visible = night > 0.5;
      base.tower.beacon.visible = this.t % 2 < 1;
    }
    // Windsock points downwind, inflates with speed.
    if (base.windsock && wind) {
      const spd = Math.hypot(wind.x, wind.z);
      const yaw = Math.atan2(-wind.z, wind.x);
      const s = base.windsock.sock;
      s.rotation.y = yaw;
      // Droop below ~2 m/s, straight out by ~8 m/s.
      s.rotation.z = -clamp01(1 - spd / 8) * 1.2;
      const infl = 0.4 + 0.6 * clamp01(spd / 8);
      s.scale.set(1, infl, infl);
    }
    // Ground crew: idle wander, scatter when buzzed.
    const crew = base.crew;
    if (crew) {
      for (const f of crew.figs) {
        if (f.flee > 0) f.flee -= dt;
        for (const p of probes) {
          const dx = p.x - f.pos.x, dz = p.z - f.pos.z;
          if (p.spd > 55 && (p.y - base.ground) < 40 && dx * dx + dz * dz < 90 * 90) {
            f.flee = 8;
            f.fleeDir = Math.atan2(f.pos.z - p.z, f.pos.x - p.x);
            break;
          }
        }
        f.phase += dt;
        let tx = f.home.x, tz = f.home.z;
        if (f.flee > 0) {
          // Bolt 25 m radially away and hold there; once the scare passes the
          // eased move below drifts them back home.
          tx = f.home.x + Math.cos(f.fleeDir) * 25;
          tz = f.home.z + Math.sin(f.fleeDir) * 25;
        } else {
          // Gentle idle wander around the home spot.
          tx += Math.cos(f.phase * 0.7) * 3;
          tz += Math.sin(f.phase) * 3;
        }
        const k = 1 - Math.exp(-(f.flee > 0 ? 6 : 1.5) * dt);
        f.pos.x += (tx - f.pos.x) * k;
        f.pos.z += (tz - f.pos.z) * k;
        f.pos.y = crew.y; // stay on the apron deck
      }
      let idx = 0;
      for (const f of crew.figs) {
        this.dummy.position.copy(f.pos);
        this.dummy.rotation.set(0, f.flee > 0 ? f.fleeDir : f.phase, 0);
        // Arms-up bob while fleeing (a quick vertical jiggle).
        this.dummy.position.y += f.flee > 0 ? Math.abs(Math.sin(this.t * 12)) * 0.4 : 0;
        this.dummy.updateMatrix();
        crew.mesh.setMatrixAt(idx++, this.dummy.matrix);
      }
      crew.mesh.instanceMatrix.needsUpdate = true;
    }
    // Gull cry audio when a flock is fleeing near the listener is handled in
    // update() globally.
  }

  // ==========================================================================
  // Frame update
  // ==========================================================================
  update(dt, svrT, sky, probes = [], wind = null) {
    if (typeof document !== "undefined" && document.hidden) return;
    // svrT is the shared server clock (seconds); until it's known, run on local
    // time so the scene still lives.
    this.t = svrT;
    this._updateBoats(dt);
    this._updateLighthouse(sky);
    this._updateGulls(dt, probes);
    this._updateWhales(dt, probes.length ? probes[0] : null);
    for (const base of this.bases) this._updateBase(dt, base, sky, probes, wind);

    // Occasional ambient audio, distance-attenuated to the listener (probe 0 =
    // local plane). Cheap synth one-shots; skipped when audio is muted/absent.
    if (this.audio && probes.length) {
      const me = probes[0];
      // A gull cry when the local plane skims low over any fleeing flock.
      this.gullCryAt -= dt;
      if (this.gullCryAt <= 0 && this.gulls) {
        for (const a of this.gulls.anchors) {
          if (a.flee > 5.4) {
            const d = Math.hypot(me.x - a.x, me.z - a.z);
            if (d < 300) {
              this.audio.gull?.((me.x - a.x) / 300);
              this.gullCryAt = 1.5;
            }
            break;
          }
        }
      }
    }
  }
}
